import {
    addDefaultFlagsToCloudinaryUrl,
    generateUrl,
    IMediaElementProps,
    TCropType
} from '@honeybook/hbui/dist/components/HBMediaElement/HBMediaElement';
import {
    FileDetails,
    TEmailProviderName,
    TServerImageCrop
} from '~/types/server.types';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { TBlock } from '~/types/builder.types';
import { IDisabledCropOptions } from '~/common/CropImageModal/CropImageModal.types';
import {
    TCropRatio,
    IFlowThumbnail,
    IImage,
    IImageDimension,
    IRatio
} from '~/types/model.types';
import type { Crop } from 'react-image-crop';
import { makeAspectCrop } from 'react-image-crop';
import { PercentCrop } from 'react-image-crop/src/types';
import { User } from '~/models/user.model';
import { IWorkspaceMember } from '~/types/angular.types';
import { ProfileImage } from '~/apps/main/Pipeline/pipeline.types';

export interface HBCrop extends Crop {
    aspect?: number;
}

export interface HBPercentCrop extends HBCrop {
    unit: '%';
}

export const CROP_RATIOS_DATA: Record<TCropRatio, IRatio> = {
    Original: {
        title: 'Original'
    },
    Custom: {
        title: 'Custom'
    },
    '4:3': {
        title: '4:3',
        value: 4 / 3,
        iconStyle: { width: '18px', height: '13px' }
    },
    '3:4': {
        title: '3:4',
        value: 3 / 4,
        iconStyle: { width: '13px', height: '18px' }
    },
    '16:9': {
        title: '16:9',
        value: 16 / 9,
        iconStyle: { width: '18px', height: '11px' }
    },
    '9:16': {
        title: '9:16',
        value: 9 / 16,
        iconStyle: { width: '11px', height: '18px' }
    },
    '1:1': {
        title: '1:1',
        value: 1,
        iconStyle: { width: '16px', height: '16px' }
    },
    Circle: {
        title: 'Circle',
        value: 1,
        iconStyle: { width: '16px', height: '16px', borderRadius: '50%' }
    }
};

export const CROP_RATIO_TITLES = Object.values(CROP_RATIOS_DATA).map(
    ratio => ratio.title
);

export const EMAIL_PROVIDERS_IMGS = {
    yahoo: '/yahoo 1.svg',
    aol: '/AOL.svg',
    outlook: '/Outlook.svg',
    eas: '/Outlook.svg',
    ews: '/Outlook.svg',
    Gmail: '/NewGmail16.svg',
    icloud: '/ICloud.svg'
} as const satisfies { [key in TEmailProviderName]?: string };

export type TEmailProvidersImgs =
    (typeof EMAIL_PROVIDERS_IMGS)[keyof typeof EMAIL_PROVIDERS_IMGS];

export function serverImageToHBMediaElement(
    image: IImage,
    forceOriginal: boolean = false
): IMediaElementProps {
    let crop: TCropType;

    if (image.crop_type && image.crop_type === 'pad') {
        // ignore crop_type === 'pad' if we'd like to display a round image
        // image must be square (and thus cropped) in order to be properly rounded
        if (image.crop_ratio === 'Circle') {
            return {
                path: image.cloudinary_public_id,
                width: Math.min(image.width, image.height),
                height: Math.min(image.width, image.height),
                x: image.position_x,
                y: image.position_y,
                crop: 'crop'
            };
        }
        // return a padded, square image with transparent bg
        return {
            path: image.cloudinary_public_id,
            width: Math.max(image.width, image.height),
            height: Math.max(image.width, image.height),
            x: 0,
            y: 0,
            crop: 'pad',
            transformations: [{ background: 'transparent' }]
        };
    }

    if (forceOriginal || !image.crop_ratio) {
        crop = null;
    } else {
        crop =
            (image.position_w !== image.width &&
                image.position_h !== image.height) ||
            image.crop_ratio !== 'Original'
                ? 'crop'
                : null;
    }

    const width = crop ? image.position_w : image.width;
    const height = crop ? image.position_h : image.height;

    return {
        path: image.cloudinary_public_id,
        width,
        height,
        x: image.position_x,
        y: image.position_y,
        crop
    };
}

export function reactCropToServerImage(
    crop: HBCrop,
    image: IImage,
    ratio: IRatio
): TServerImageCrop {
    return {
        position_x: Math.round((crop.x / 100) * image.width),
        position_y: Math.round((crop.y / 100) * image.height),
        position_w: Math.round((crop.width / 100) * image.width),
        position_h: Math.round((crop.height / 100) * image.height),
        crop_ratio: ratio.title
    };
}

export function calculateImageStyleByContainerLimits(
    image: IImage,
    widthLimit: number,
    heightLimit: number
) {
    const imageRatio = image.width / image.height;
    const desiredImageHeight = widthLimit / imageRatio;

    if (desiredImageHeight > heightLimit) {
        return {
            width: heightLimit * imageRatio,
            height: heightLimit
        };
    }

    return {
        width: widthLimit,
        height: desiredImageHeight
    };
}

export function restoreImageCropping(
    image: IImage,
    croppedImageWidth: number,
    croppedImageHeight: number,
    isCircleMode: boolean = false,
    aspect: number = null
) {
    const imageStyle = calculateImageStyleByContainerLimits(
        image,
        croppedImageWidth,
        croppedImageHeight
    );

    const aspectData = isCircleMode
        ? CROP_RATIOS_DATA.Circle
        : CROP_RATIOS_DATA[image.crop_ratio];

    let initialCrop: Partial<HBCrop>;

    if (image.crop_ratio) {
        initialCrop = {
            x: ((image.position_x || 0) / image.width) * 100,
            y: ((image.position_y || 0) / image.height) * 100,
            width: ((image.position_w || image.width) / image.width) * 100,
            height: ((image.position_h || image.height) / image.height) * 100,
            unit: '%',
            aspect: aspectData?.value || null
        };
    } else if (isCircleMode) {
        initialCrop = {
            x: 0,
            y: 0,
            width: 100,
            height: 100,
            unit: '%',
            aspect: 1
        };
    } else {
        let cropWidth = 100;
        let cropHeight = 100;

        if (aspect) {
            const imageRatio = image.width / image.height;
            if (imageRatio > aspect) {
                cropWidth = (aspect / imageRatio) * 100;
                cropHeight = 100;
            } else {
                cropWidth = 100;
                cropHeight = (imageRatio / aspect) * 100;
            }
        }

        initialCrop = {
            x: (100 - cropWidth) / 2,
            y: (100 - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
            unit: '%',
            aspect
        };
    }

    return {
        initialCrop: initialCrop as HBCrop,
        aspectData: aspectData || CROP_RATIOS_DATA.Original,
        imageStyle
    };
}

export const getOriginalWidth = (image: IImage) => {
    if ((!image.crop_ratio || image.crop_ratio === 'Original') && image.width) {
        return image.width;
    }
    return image.position_w;
};

const getOriginalHeight = (image: IImage) => {
    if (
        (!image.crop_ratio || image.crop_ratio === 'Original') &&
        image.height
    ) {
        return image.height;
    }
    return image.position_h;
};

export const getResizeValues = (image: IImage) => {
    const resizeRatio = image?.resize_ratio;
    const cropRatio = image?.crop_ratio;

    // In case we have a circled image / circled placeholder,
    // But there is no resize, we want to initial the image with 150/150
    if (!resizeRatio && cropRatio === 'Circle') {
        return {
            width: 150,
            height: 150
        };
    }

    return resizeRatio
        ? {
              width: getOriginalWidth(image) * resizeRatio,
              height: getOriginalHeight(image) * resizeRatio
          }
        : null;
};

export const getOneToOneRatioImageAttrs = (
    image: IImage,
    currentImage: IImage
) => {
    const smallerEdge = image.width > image.height ? image.height : image.width;

    return {
        ...image,
        crop_ratio: currentImage.crop_ratio,
        position_h: smallerEdge,
        position_w: smallerEdge,
        position_x: 0,
        position_y: 0
    };
};

interface IGetImageDimensionProps {
    isDesktop: boolean;
    isSubItem: boolean;
    isServiceSelectionColumnLayout: boolean;
}

export const getImageDimensionForInvoiceServiceItem = ({
    isDesktop,
    isSubItem
}: Omit<
    IGetImageDimensionProps,
    'isServiceSelectionColumnLayout'
>): IImageDimension => {
    if (isDesktop) {
        return isSubItem
            ? { height: '50px', width: '50px' }
            : { height: '80px', width: '80px' };
    }
    return isSubItem
        ? { height: '32px', width: '32px' }
        : { height: '48px', width: '48px' };
};

export function getImageDimensionForServiceSelectionItem({
    isDesktop,
    isSubItem,
    isServiceSelectionColumnLayout
}: IGetImageDimensionProps): IImageDimension {
    if (isServiceSelectionColumnLayout) {
        return { height: '200px', width: '100%' };
    }

    if (isDesktop) {
        return isSubItem
            ? { height: '60px', width: '60px' }
            : { height: '150px', width: '150px' };
    }
    return isSubItem
        ? { height: '32px', width: '32px' }
        : { height: '48px', width: '48px' };
}

export function getCropModalDisabledRatios(
    block: TBlock
): IDisabledCropOptions {
    if (block.isRootBlock) {
        return {
            disabledRatioTitles: ['Circle'],
            disabledTooltipText:
                'Background images can’t be in circle shape since they wouldn’t occupy the entire background'
        };
    }
    switch (block.type) {
        case 'container_block':
            return {
                disabledRatioTitles: ['Circle'],
                disabledTooltipText:
                    'Background images can’t be in circle shape since they wouldn’t occupy the entire background'
            };
        case 'invoice_service_item_block':
            const notAllowedInvoiceImageRatios: TCropRatio[] = [
                'Original',
                'Custom',
                '4:3',
                '3:4',
                '16:9',
                '9:16'
            ];
            return {
                disabledRatioTitles: CROP_RATIO_TITLES.filter(
                    (ratio: TCropRatio) =>
                        notAllowedInvoiceImageRatios.includes(ratio)
                ),
                disabledTooltipText:
                    'Images for items and packages must be in a 1:1 aspect ratio. Use a square, circle or the corner radius slider in the sidebar'
            };
        case 'service_selection_item_block':
            // eslint-disable-next-line no-case-declarations
            const allowedRatios = (block as ServiceSelectionItemBlock)
                .allowedCropRatios.types;

            return {
                disabledRatioTitles: CROP_RATIO_TITLES.filter(
                    (title: TCropRatio) => !allowedRatios.includes(title)
                ),
                disabledTooltipText: (block as ServiceSelectionItemBlock)
                    .isColumnLayout
                    ? 'Images for items and packages in column layout can only have a 4:3 ratio. You can use a circle crop as well.'
                    : 'Images for items and packages in row layout can only have a 1:1 ratio. You can use a square, circle or the corner radius slider in the sidebar to crop the image.'
            };
        default:
            return {
                disabledRatioTitles: []
            };
    }
}

export const getInitialResizeRatio = (image: IImage): number => {
    // normalizes bigger images to be inserted first time into the builder in a set size
    // to prevent builder content to shift when big image is loaded.
    const { height, width, resize_ratio } = image;
    if (resize_ratio) {
        return resize_ratio;
    }

    const PORTRAIT_IMAGE_WIDTH = 200;
    const LANDSCAPE_IMAGE_WIDTH = 400;

    const isLandscapeImage = height <= width; // we treat 1:1 as landscape

    const initialWidth = isLandscapeImage
        ? LANDSCAPE_IMAGE_WIDTH
        : PORTRAIT_IMAGE_WIDTH;

    if (width <= initialWidth) {
        // we don't enlarge smaller pictures
        return null;
    }

    return initialWidth / width;
};

export const getOptimizedThumbnailImage = (
    thumbnail: IFlowThumbnail,
    renderedDimensions: { height: number; width: number }
) => {
    const resizeTransformation = {
        ...renderedDimensions,
        crop: 'fill',
        dpr: '2.0'
    };

    if (thumbnail?.cloudinary_public_id) {
        // The current implementation of HBMediaElement is centered on the crop transformation
        // but this is not generic. I use this function to get the data for the first transformation (crop)
        const { path, ...cropTransformation } =
            serverImageToHBMediaElement(thumbnail);

        return generateUrl({
            path,
            transformations: [
                { fetchFormat: 'auto', flags: 'lossy', quality: 'auto' },
                cropTransformation,
                resizeTransformation
            ]
        });
    }

    return addDefaultFlagsToCloudinaryUrl(thumbnail?.cloudinary_url, [
        { fetchFormat: 'auto', flags: 'lossy', quality: 'auto' },
        resizeTransformation
    ]);
};

// In the new version of react crop it doesn't autofill or prevent dimension overflow of the crop selection,
//  now you need to manually do it when you give manual values.
export function getAspectHBCrop({
    crop,
    aspect,
    imageDimensions
}: {
    crop: PercentCrop;
    aspect: number;
    imageDimensions: { width: number; height: number };
}) {
    if (!aspect) {
        return { ...crop, aspect };
    }

    const aspectCrop = makeAspectCrop(
        crop,
        aspect,
        imageDimensions.width,
        imageDimensions.height
    ) as PercentCrop;

    return { ...aspectCrop, aspect };
}

export function isObjectAnImage(item: any) {
    if (!item) {
        return false;
    }

    return item.hasOwnProperty('cloudinary_public_id');
}

// this function is from avatar_service.js in angular
export function isDefaultProfileImage(user: User | IWorkspaceMember) {
    return isDefaultProfileImageInner(user.profile_image);
}

// this function is from avatar_service.js in angular
export function isDefaultProfileImageInner(
    profileImage: ProfileImage | IImage
) {
    // image is the same as default empty or the template users
    var isTimeStampedBowtieProfileImage =
        profileImage &&
        profileImage.url &&
        profileImage.url.includes(
            '/honeybook_cdn/assets_users_production/users/'
        );

    return (
        profileImage == null ||
        profileImage.url ===
            'https://honeybook_cdn.s3.amazonaws.com/assets_users_production/users/empty_default_image.png' ||
        profileImage.original_url ===
            'https://s3.amazonaws.com/honeybook_cdn/assets_users_demo/users/sample_user/profilepic180x180.png' ||
        isTimeStampedBowtieProfileImage
    );
}

export function isDefaultProfileImageURL(profileImageURL) {
    const defaultProfileImageUrl =
        'https://honeybook_cdn.s3.amazonaws.com/assets_users_production/users/empty_default_image.png';

    return profileImageURL === defaultProfileImageUrl;
}

export const generateImageDownloadUrl = (url: string) => {
    return isCloudinaryImage(url)
        ? addDefaultFlagsToCloudinaryUrl(url, [{ flags: 'attachment' }])
        : url;
};

export const isCloudinaryImage = url =>
    url?.indexOf('res.cloudinary.com/honeybook') > -1;

export function translateImageToFileDetailsPayload(
    images: IImage[]
): FileDetails[] {
    return images.map(image => ({
        filename: image.filename,
        filesize: image.filesize,
        filetype: image.filetype,
        type: 'cover',
        url: image.url || image.cloudinary_url,
        cloudinary_public_id: image.cloudinary_public_id,
        cloudinary_sub_account: image.cloudinary_sub_account,
        cloudinary_url: image.cloudinary_url,
        height: image.height,
        width: image.width,
        phash: image.phash
    }));
}
